/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (design 14–16): error screens outside the call, with iON texts on
 * Element Call's ErrorView.
 */

import { type FC, type ReactElement, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@vector-im/compound-web";
import {
  ErrorSolidIcon,
  PopOutIcon,
  UnknownSolidIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";

import { ErrorView } from "../ErrorView";
import { useGraylog } from "../logging/Graylog";
import { Config } from "../config/Config";
import { reportCallProblem } from "./IonCallErrorPage";
import { ACTION_TEXTS, OTHER_ERROR_TEXTS, pick } from "./callErrorTexts";

/** 14: something failed outside the call; trying again reloads. */
export const IonGenericError: FC<{ error: Error }> = ({
  error,
}): ReactElement => {
  const { i18n } = useTranslation();
  const graylog = useGraylog();
  const [reported, setReported] = useState(false);
  const texts = OTHER_ERROR_TEXTS.generic;
  const onReport = useCallback(() => {
    setReported(true);
    void reportCallProblem(graylog, error);
  }, [graylog, error]);
  return (
    <ErrorView
      Icon={ErrorSolidIcon}
      title={pick(texts.title, i18n.language)}
      fatal
    >
      <p>{pick(texts.description, i18n.language)}</p>
      <Button kind="primary" onClick={(): void => window.location.reload()}>
        {pick(ACTION_TEXTS.retry, i18n.language)}
      </Button>
      <Button kind="secondary" onClick={onReport} disabled={reported}>
        {pick(
          reported ? ACTION_TEXTS.reported : ACTION_TEXTS.report,
          i18n.language,
        )}
      </Button>
    </ErrorView>
  );
};

/** 15: the link leads to no call. */
export const IonCallNotFound: FC = (): ReactElement => {
  const { i18n } = useTranslation();
  const texts = OTHER_ERROR_TEXTS.callNotFound;
  return (
    <ErrorView Icon={UnknownSolidIcon} title={pick(texts.title, i18n.language)}>
      <p>{pick(texts.description, i18n.language)}</p>
      {import.meta.env.VITE_PACKAGE !== "embedded" && (
        <Button
          as="a"
          kind="primary"
          href={Config.get().call_links?.download_url ?? "https://i-on.chat"}
          target="_blank"
          rel="noopener noreferrer"
        >
          {pick(ACTION_TEXTS.getIon, i18n.language)}
        </Button>
      )}
    </ErrorView>
  );
};

/**
 * 16: the call is open in another tab. Continuing here reloads, and the
 * page loading here sends the other tab to this same screen.
 */
export const IonOpenElsewhere: FC = (): ReactElement => {
  const { i18n } = useTranslation();
  const texts = OTHER_ERROR_TEXTS.openElsewhere;
  return (
    <ErrorView Icon={PopOutIcon} title={pick(texts.title, i18n.language)}>
      <p>{pick(texts.description, i18n.language)}</p>
      <Button kind="primary" onClick={(): void => window.location.reload()}>
        {pick(ACTION_TEXTS.continueHere, i18n.language)}
      </Button>
    </ErrorView>
  );
};
